/**
 * The Issue approval list in two views (EXV-ISSUE-2).
 *
 * "To approve" is the caller's own queue (rows routed to the caller's roles). "All pending" is every pending row of the
 * plant, read only, offered only when the server says the caller may see it (`can_see_all`: read on the list's
 * module). Each row names the role it waits on; Approve / Reject show only where the caller can act. An empty view
 * names whom the plant's queue waits on.
 */
import React, { useCallback, useState } from 'react';
import { Segmented } from 'antd';
import BatchListTab from './BatchListTab';
import { issueApprovalColumns } from './columns';

const MINE = 'mine';
const ALL = 'all';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "2 batches wait on Production Manager; 1 batch waits on QA Officer" */
export const waitingSummary = (roles = []) =>
  roles
    .filter((r) => r.count > 0)
    .map((r) => `${plural(r.count, 'batch', 'batches')} ${r.count === 1 ? 'waits' : 'wait'} on ${r.role_name || 'an unnamed role'}`)
    .join('; ');

export const issueEmptyMessage = (scope, roles) => {
  const summary = waitingSummary(roles);
  if (scope === ALL) return 'No batches are waiting for issue approval.';
  if (summary) return `Nothing is waiting on your roles. ${summary} — open “All pending” to see them.`;
  return 'Nothing is waiting on your roles.';
};

export default function IssueApprovalTab({ load, reloadKey, toolbar, columnsProps, ...listProps }) {
  const [scope, setScope] = useState(MINE);
  const [meta, setMeta] = useState({ canSeeAll: false, waitingOnRoles: [] });

  const read = useCallback(
    (pg) =>
      load(pg, scope).then((r) => {
        setMeta({ canSeeAll: r.canSeeAll, waitingOnRoles: r.waitingOnRoles });
        return r;
      }),
    [load, scope]
  );

  const control = meta.canSeeAll || scope === ALL ? (
    <Segmented
      data-testid="bmrd-issue-scope"
      value={scope}
      onChange={setScope}
      options={[
        { label: 'To approve', value: MINE },
        { label: 'All pending', value: ALL },
      ]}
    />
  ) : null;

  return (
    <BatchListTab
      testId="bmrd-issue-approval"
      rowKey="approval_id"
      what={scope === ALL ? 'the batches awaiting issue approval' : 'the batches awaiting your issue approval'}
      emptyMessage={issueEmptyMessage(scope, meta.waitingOnRoles)}
      reloadKey={`${reloadKey}|${scope}`}
      load={read}
      toolbar={
        <>
          {control}
          {toolbar}
        </>
      }
      columns={issueApprovalColumns(columnsProps)}
      {...listProps}
    />
  );
}
